import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { User, CreditCard, Users, Car, FileText, Building, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { InfoRow } from './InfoRow';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_LIST = [
  { key: 'driver_id', label: 'Driver ID' },
  { key: 'drivers_license', label: 'Drivers License' },
  { key: 'owner_id', label: 'Owner ID' },
  { key: 'enatis', label: 'eNatis' },
  { key: 'vehicle_images', label: 'Vehicle Images' },
  { key: 'tracker_certificate', label: 'Tracker Certificate' },
  { key: 'license_disc', label: 'License Disc' },
  { key: 'page_28', label: 'Page 28' },
  { key: 'criminal_checks', label: 'Criminal Checks' },
];

export const ViewDriverDialog = ({ open, onOpenChange, selectedDriver, onEdit, onRefresh }) => {
  const handleUpload = async (docKey, file, driverId, docLabel) => {
    const formData = new FormData();
    formData.append('doc_type', docKey);
    formData.append('file', file);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/drivers/${driverId}/documents`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${docLabel} uploaded!`);
      onRefresh();
    } catch {
      toast.error('Upload failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Driver Details</DialogTitle>
          <DialogDescription>
            Complete information for {selectedDriver?.driver_name}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          {selectedDriver && (
            <div className="space-y-6">
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <User className="h-4 w-4" /> Owner Details
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <InfoRow label="OWNER USERNAME" value={selectedDriver.owner_username} />
                  <InfoRow label="OWNER NAME" value={selectedDriver.owner_name} />
                  <InfoRow label="OWNER ID NUMBER" value={selectedDriver.owner_id_number} />
                  <InfoRow label="OWNER ADDRESS" value={selectedDriver.owner_address} />
                  <InfoRow label="OWNER CELL" value={selectedDriver.owner_contact} />
                  <InfoRow label="COMPANY TRADING AS" value={selectedDriver.company_trading_as} />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <CreditCard className="h-4 w-4" /> Bank Details
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <InfoRow label="BANK NAME" value={selectedDriver.bank_name} />
                  <InfoRow label="ACCOUNT NUMBER" value={selectedDriver.account_number} />
                  <InfoRow label="BRANCH CODE" value={selectedDriver.branch_code} />
                  <InfoRow label="ACCOUNT TYPE" value={selectedDriver.account_type} />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <Users className="h-4 w-4" /> Driver Details
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <InfoRow label="DRIVER NAME" value={selectedDriver.driver_name} />
                  <InfoRow label="DRIVER ID NUMBER" value={selectedDriver.driver_id_number} />
                  <InfoRow label="DRIVER CONTACT NUMBER" value={selectedDriver.driver_contact} />
                  <InfoRow label="DRIVER ADDRESS" value={selectedDriver.driver_address} />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <FileText className="h-4 w-4" /> Documents
                </h4>
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  {DOC_LIST.map(doc => {
                    const docVal = selectedDriver[`doc_${doc.key}`] || '';
                    const hasFile = docVal.startsWith('/api/');
                    return (
                      <div key={doc.key} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium w-40">{doc.label}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${hasFile ? 'bg-green-100 text-green-700' : docVal === 'Yes' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                            {hasFile ? 'Uploaded' : docVal || 'No'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {hasFile && (
                            <a href={`${API_URL}${docVal}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600">
                              View
                            </a>
                          )}
                          <label className="text-xs px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 cursor-pointer">
                            Upload
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp" className="hidden" onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleUpload(doc.key, file, selectedDriver.id, doc.label);
                            }} />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <Car className="h-4 w-4" /> Vehicle Details
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <InfoRow label="REGISTRATION NUMBER" value={selectedDriver.registration_number} />
                  <InfoRow label="VEHICLE TYPE" value={selectedDriver.vehicle_type} />
                  <InfoRow label="VEHICLE MAKE" value={selectedDriver.vehicle_make} />
                  <InfoRow label="VEHICLE MODEL" value={selectedDriver.vehicle_model} />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <FileText className="h-4 w-4" /> Finance & Comments
                </h4>
                <div className="grid grid-cols-1 gap-4 bg-slate-50 p-4 rounded-lg">
                  <InfoRow label="FINANCE COMMENTS" value={selectedDriver.finance_comments} />
                  <InfoRow label="COMMENT" value={selectedDriver.general_comments} />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-slate-800">
                  <Building className="h-4 w-4" /> Account Status
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                  <InfoRow label="STATUS" value={selectedDriver.is_active ? 'Active' : 'Inactive'} />
                  <InfoRow label="CREATED AT" value={selectedDriver.created_at ? new Date(selectedDriver.created_at).toLocaleDateString() : '-'} />
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={() => { onOpenChange(false); onEdit(selectedDriver); }}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Driver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
